import React, { ComponentType, ReactElement, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Checkbox } from '@ui/components/ui/checkbox';
import { matchesSubstring, MultiSelectField, MultiSelectOption } from '@ui/components/ui/multi-select';

import { cardClass } from '@app/constants/styles';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { ProductEntity, UpdateProduct } from '@data';
import {
  useAikidoRepositories,
  useGithubRepositories,
  useOrganizationAikidoRepositories,
  useOrganizationGithubRepositories,
  useOrganizationSentryProjects,
  useSentryProjects,
  useUnlinkProductSentryToken,
  useUpdateProductDetail,
} from '@data/hooks/products';

import ConfirmDialog from '../../ConfirmDialog';
import SentryTokenDialog from './SentryTokenDialog';
import { AikidoIcon, GithubIcon, SentryIcon } from '../toolIcons';
import { toRepositoryFullName } from '../githubLink';
import { projectLabel, projectsWithMetric, searchSeedFor, suggestBrowserProject } from '../sentryLink';

/** What every row's control needs: how to save, and whether the reader may. */
/** What a product is connected to — the fields, without the product they may not belong to yet. */
export type ProductConnections = Pick<
  ProductEntity,
  | 'repositories'
  | 'aikidoRepositories'
  | 'sentryOrgSlug'
  | 'sentryProjectSlugs'
  | 'sentryBrowserProjectSlug'
  // Read-only here: its own route writes these, since a token is tried before it is stored.
  | 'hasExternalSentryToken'
  | 'sentryTokenLinkedAt'
  | 'sentryTokenLinkedByName'
>;

/** Where the pickers read their lists from: a saved product asks by its own id, a new one asks the organization. */
export type PickerSource =
  | { productId: string; organizationId?: undefined }
  | { organizationId: string; productId?: undefined };

type RowProps = {
  product: ProductConnections;
  source: PickerSource;
  canEdit: boolean;
  isPending: boolean;
  patch: (values: UpdateProduct) => void;
};

/* Both are called every render, the idle one fetching nothing, because a hook cannot be called conditionally. */
/** Beside the connections rather than in them: a product being added has a name in the form and nothing else. */
type SeededRowProps = RowProps & { productName: string };

const useRepositories = (source: PickerSource) => {
  const byProduct = useGithubRepositories(source.productId);
  const byOrganization = useOrganizationGithubRepositories(source.organizationId);
  return source.productId ? byProduct : byOrganization;
};

const useScannedRepositories = (source: PickerSource) => {
  const byProduct = useAikidoRepositories(source.productId);
  const byOrganization = useOrganizationAikidoRepositories(source.organizationId);
  return source.productId ? byProduct : byOrganization;
};

const useProjects = (source: PickerSource) => {
  const byProduct = useSentryProjects(source.productId);
  const byOrganization = useOrganizationSentryProjects(source.organizationId);
  return source.productId ? byProduct : byOrganization;
};

/** Empty keys are dropped, `null` never reaches a create, and the Sentry org goes only with a link it qualifies. */
export const connectionsToPayload = (value: ProductConnections): Partial<ProductConnections> => {
  const payload: Partial<ProductConnections> = {};
  if (value.repositories?.length) payload.repositories = value.repositories;
  if (value.aikidoRepositories?.length) payload.aikidoRepositories = value.aikidoRepositories;
  if (value.sentryProjectSlugs?.length) payload.sentryProjectSlugs = value.sentryProjectSlugs;
  if (value.sentryBrowserProjectSlug) payload.sentryBrowserProjectSlug = value.sentryBrowserProjectSlug;
  if (value.sentryOrgSlug && (payload.sentryProjectSlugs || payload.sentryBrowserProjectSlug)) {
    payload.sentryOrgSlug = value.sentryOrgSlug;
  }
  return payload;
};

/** The rows speak the update contract, where `null` disconnects; a draft has nothing to disconnect. */
export const mergeConnections = (current: ProductConnections, values: UpdateProduct): ProductConnections => ({
  ...current,
  ...(values.repositories !== undefined ? { repositories: values.repositories } : {}),
  ...(values.aikidoRepositories !== undefined ? { aikidoRepositories: values.aikidoRepositories } : {}),
  ...(values.sentryOrgSlug !== undefined ? { sentryOrgSlug: values.sentryOrgSlug ?? undefined } : {}),
  ...(values.sentryProjectSlugs !== undefined ? { sentryProjectSlugs: values.sentryProjectSlugs ?? undefined } : {}),
  ...(values.sentryBrowserProjectSlug !== undefined
    ? { sentryBrowserProjectSlug: values.sentryBrowserProjectSlug ?? undefined }
    : {}),
});

/* `transition-none`: a duration with no transition-property means `all`, sliding the row on every change. */
const enterAnimation = 'transition-none duration-200 animate-in fade-in-0';

/* Its own animation name: picker → linked keeps the element, and an animation restarts only on a new name. */
const pickerAnimation = 'transition-none [animation:vt-fade-in_200ms_ease-out]';

/* Connect keeps its transitions: `transition-none` would beat the button's own `transition-all` through twMerge. */
const buttonAnimation = 'duration-200 animate-in fade-in-0';

const pickerClass = `min-w-0 flex-1 space-y-2 sm:max-w-sm ${pickerAnimation}`;

/** Saved repositories first, keeping one the tool stopped offering. Saved, not the draft, so a tick moves nothing. */
const withLinked = (options: MultiSelectOption[], linked: string[]): MultiSelectOption[] => {
  const missing = linked.filter(value => value && !options.some(option => option.value === value));
  const isLinked = (option: MultiSelectOption): boolean => linked.includes(option.value);
  return [
    ...missing.map(value => ({ value, label: value })),
    ...options.filter(isLinked),
    ...options.filter(option => !isLinked(option)),
  ];
};

/** Which empty line it is: the search narrowed the list to nothing, or the source had nothing to begin with. */
const emptyListKey = ({
  isLoading,
  isError,
  hasOptions,
  sourceKey,
}: {
  isLoading: boolean;
  isError: boolean;
  hasOptions: boolean;
  sourceKey: string;
}): string => {
  if (isLoading) return 'connectedApps.repositoriesLoading';
  if (!hasOptions) return isError ? 'connectedApps.unreachable' : sourceKey;
  return 'connectedApps.noSearchMatch';
};

/** Only suggest a value this tool lists: a seed the picker cannot show reads empty while Save still writes it. */
const suggestionFrom = (candidate: string | undefined, options: MultiSelectOption[]): string =>
  candidate && options.some(option => option.value === candidate) ? candidate : '';

/** One tool's row: its mark, what it is, and the control that connects it. */
/** An open picker: in the row on a wide screen, in a sheet with its Save and Cancel at the foot on a phone. */
const Picker = ({
  title,
  onCancel,
  actions,
  children,
}: {
  title: string;
  onCancel: () => void;
  actions: ReactNode;
  children: ReactNode;
}): ReactElement => {
  const isMobile = useIsMobile();
  if (!isMobile) {
    return (
      <div className={pickerClass}>
        {children}
        {actions}
      </div>
    );
  }
  return (
    <Dialog open onOpenChange={next => !next && onCancel()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="min-w-0 space-y-2">{children}</div>
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ToolRow = ({
  icon: Icon,
  name,
  hint,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  name: string;
  hint: string;
  children: ReactNode;
}): ReactElement => (
  // Stacked on a phone: the name and hint keep the row's width, and the control sits under them.
  <div className="flex flex-col items-start gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center">
    <div className="flex w-full min-w-0 flex-1 items-center gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{name}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
    </div>
    {children}
  </div>
);

/** The row's resting state once something is linked: what it points at, and the two ways to change it. */
const Linked = ({
  label,
  canEdit,
  isPending,
  onEdit,
  onDisconnect,
}: {
  label: string;
  canEdit: boolean;
  isPending: boolean;
  onEdit: () => void;
  onDisconnect: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <div className={`flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-2 ${enterAnimation} zoom-in-95`}>
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        <Check className="size-3.5" />
        {label}
      </span>
      {canEdit ? (
        <>
          <Button variant="ghost" size="sm" onClick={onEdit} disabled={isPending}>
            {t('edit')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDisconnect} disabled={isPending}>
            {t('connectedApps.disconnect')}
          </Button>
        </>
      ) : null}
    </div>
  );
};

/** Save and Cancel under an open picker. Save waits for a pick; clearing is what Disconnect is for. */
const PickerActions = ({
  canSave,
  isPending,
  onSave,
  onCancel,
}: {
  canSave: boolean;
  isPending: boolean;
  onSave: () => void;
  onCancel: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    // In a phone's sheet footer the two share its width, Cancel first as the dialogs have it.
    <div className="flex items-center gap-2 pt-1 max-sm:w-full max-sm:flex-row-reverse max-sm:pt-0 max-sm:*:flex-1">
      <Button size="sm" onClick={onSave} disabled={isPending || !canSave}>
        {t('actions.save')}
      </Button>
      <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
        {t('actions.cancel')}
      </Button>
    </div>
  );
};

const ConnectButton = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Button variant="outline" size="sm" className={`shrink-0 ${buttonAnimation}`} onClick={onClick} disabled={disabled}>
      {t('connectedApps.connect')}
    </Button>
  );
};

/** The GitHub row. Always stored as `owner/name`, the key Aikido's list and every later reader match on. */
const GithubConnection = ({
  product,
  productName,
  source,
  canEdit,
  isPending,
  patch,
}: SeededRowProps): ReactElement | null => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string[] | null>(null);
  const { data, isLoading, isError } = useRepositories(source);

  // Normalised on the way in, so a link stored as a URL still matches an option instead of reading empty.
  const linked = (product.repositories ?? []).map(toRepositoryFullName);
  const options = withLinked(
    (data?.items ?? []).map(repository => ({
      value: repository.fullName,
      label: [
        repository.fullName,
        repository.language ? `· ${repository.language}` : '',
        repository.isArchived ? `(${t('connectedApps.archived')})` : '',
      ]
        .filter(Boolean)
        .join(' '),
    })),
    linked,
  );

  // Falls back to whatever Aikido points at, the mirror of the same courtesy in the row below.
  const open = (): void => {
    if (linked.length) {
      setDraft(linked);
      return;
    }
    const suggestion = suggestionFrom((product.aikidoRepositories ?? [])[0], options);
    setDraft(suggestion ? [suggestion] : []);
  };

  if (draft !== null) {
    return (
      <Picker
        title={t('connectedApps.github')}
        onCancel={() => setDraft(null)}
        actions={
          <PickerActions
            canSave={draft.length > 0}
            isPending={isPending}
            onSave={() => {
              patch({ repositories: draft });
              setDraft(null);
            }}
            onCancel={() => setDraft(null)}
          />
        }
      >
        <MultiSelectField
          label={t('connectedApps.repositories')}
          options={options}
          value={draft}
          onChange={setDraft}
          emptyLabel={isLoading ? t('connectedApps.repositoriesLoading') : t('connectedApps.pickRepositories')}
          searchable
          // A fuzzy score answers "phill" with every name those letters appear inside; substring is honest.
          filter={matchesSubstring}
          searchPlaceholder={t('connectedApps.searchRepositories')}
          emptySearchLabel={t(
            emptyListKey({
              isLoading,
              isError,
              hasOptions: options.length > 0,
              sourceKey: 'connectedApps.noRepositories',
            }),
          )}
          // Nothing picked means the whole organisation is on offer, so the search opens on the product's name.
          defaultSearch={draft.length ? undefined : searchSeedFor(productName)}
          // One repository reads as its name; two already outgrow a field this narrow.
          summaryThreshold={1}
          renderCountSummary={count => t('connectedApps.nRepositories', { count })}
          // Emptying would leave Save disabled with no way back; coming off GitHub is what Disconnect is for.
          clearable={false}
          disabled={isLoading}
        />
        {/* Without the names, a shorter list reads as a client who granted us nothing. */}
        {data?.failedAccounts?.length ? (
          <p className="text-xs text-destructive">
            {t('connectedApps.accountsUnreadable', { accounts: data.failedAccounts.join(', ') })}
          </p>
        ) : null}
      </Picker>
    );
  }

  if (linked.length) {
    return (
      <Linked
        label={linked.length === 1 ? linked[0] : t('connectedApps.nRepositories', { count: linked.length })}
        canEdit={canEdit}
        isPending={isPending}
        onEdit={open}
        onDisconnect={() => patch({ repositories: [] })}
      />
    );
  }

  return canEdit ? <ConnectButton onClick={open} disabled={isPending} /> : null;
};

/** The Aikido row. Its own link, because wiring up one tool must not silently decide what another reads. */
const AikidoConnection = ({
  product,
  productName,
  source,
  canEdit,
  isPending,
  patch,
}: SeededRowProps): ReactElement | null => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string[] | null>(null);
  const { data, isLoading, isError } = useScannedRepositories(source);

  const linked = product.aikidoRepositories ?? [];
  const offered = data?.items ?? [];
  const onGithub = (product.repositories ?? []).map(toRepositoryFullName);

  // Aikido scans GitHub repositories, so the product's own are the default when Aikido offers none.
  const options = withLinked(
    offered.length
      ? offered.map(repository => ({
          value: repository.fullName,
          label: repository.lastScannedAt
            ? repository.fullName
            : `${repository.fullName} (${t('connectedApps.neverScanned')})`,
        }))
      : onGithub.map(fullName => ({ value: fullName, label: fullName })),
    linked,
  );

  // One repository on GitHub is a default worth a click; several would be a guess, so the field opens empty.
  const open = (): void => {
    if (linked.length) {
      setDraft([linked[0]]);
      return;
    }
    const suggestion = onGithub.length === 1 ? suggestionFrom(onGithub[0], options) : '';
    setDraft(suggestion ? [suggestion] : []);
  };

  if (draft !== null) {
    return (
      <Picker
        title={t('connectedApps.aikido')}
        onCancel={() => setDraft(null)}
        actions={
          <PickerActions
            canSave={draft.length > 0}
            isPending={isPending}
            onSave={() => {
              patch({ aikidoRepositories: draft });
              setDraft(null);
            }}
            onCancel={() => setDraft(null)}
          />
        }
      >
        <MultiSelectField
          // Aikido reads one repository, so the same control as the row above, in its single-choice mode.
          single
          label={t('connectedApps.aikidoRepositories')}
          options={options}
          value={draft}
          onChange={setDraft}
          emptyLabel={isLoading ? t('connectedApps.repositoriesLoading') : t('connectedApps.pickRepository')}
          searchable
          filter={matchesSubstring}
          searchPlaceholder={t('connectedApps.searchRepositories')}
          emptySearchLabel={t(
            emptyListKey({
              isLoading,
              isError,
              hasOptions: options.length > 0,
              sourceKey: 'connectedApps.aikidoNoRepositories',
            }),
          )}
          defaultSearch={draft.length ? undefined : searchSeedFor(productName)}
          disabled={isLoading}
        />
      </Picker>
    );
  }

  if (linked.length) {
    return (
      <Linked
        label={linked.length === 1 ? linked[0] : t('connectedApps.nRepositories', { count: linked.length })}
        canEdit={canEdit}
        isPending={isPending}
        onEdit={open}
        onDisconnect={() => patch({ aikidoRepositories: [] })}
      />
    );
  }

  return canEdit ? <ConnectButton onClick={open} disabled={isPending} /> : null;
};

/** A way in when the product lives in a Sentry organization our own token cannot see. */
const ExternalToken = ({
  product,
  productId,
  canEdit,
}: {
  product: ProductConnections;
  productId: string;
  canEdit: boolean;
}): ReactElement | null => {
  const { t } = useTranslation();
  const [tokenOpen, setTokenOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const unlink = useUnlinkProductSentryToken(productId);

  if (!canEdit) return null;

  // Mounted outside the branch: linking flips `hasExternalSentryToken` and would tear the dialog off mid-confirmation.
  return (
    <>
      {product.hasExternalSentryToken ? (
        <div className="flex flex-col gap-0.5 text-xs">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-muted-foreground">{t('connectedApps.sentryExternalLinked')}</span>
            <button
              type="button"
              onClick={() => setUnlinkOpen(true)}
              className="text-destructive underline hover:no-underline"
            >
              {t('connectedApps.sentryUnlink')}
            </button>
          </div>
          {/* Whose token: a personal one carries its owner's access and keeps working after they go. */}
          {product.sentryTokenLinkedByName && (
            <span className="text-muted-foreground/80">
              {t('connectedApps.sentryExternalLinkedBy', {
                name: product.sentryTokenLinkedByName,
                date: product.sentryTokenLinkedAt ? new Date(product.sentryTokenLinkedAt).toLocaleDateString() : '',
              })}
            </span>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setTokenOpen(true)}
          className="self-start text-xs text-muted-foreground underline hover:text-foreground"
        >
          {t('connectedApps.sentryExternalPrompt')}
        </button>
      )}

      <SentryTokenDialog productId={productId} open={tokenOpen} onOpenChange={setTokenOpen} />
      <ConfirmDialog
        open={unlinkOpen}
        onOpenChange={setUnlinkOpen}
        title={t('connectedApps.sentryUnlinkTitle')}
        description={t('connectedApps.sentryUnlinkBody')}
        confirmLabel={t('connectedApps.sentryUnlink')}
        onConfirm={() => {
          unlink.mutate();
          setUnlinkOpen(false);
        }}
      />
    </>
  );
};

type SentryDraft = {
  sentryOrgSlug: string | null;
  sentryProjectSlugs: string[];
  sentryBrowserProjectSlug: string | null;
};

/** The Sentry row. Two fields behind one Save: response time reads the backend projects, crash-free sessions the frontend one. */
const SentryConnection = ({
  product,
  productName,
  source,
  canEdit,
  isPending,
  patch,
}: SeededRowProps): ReactElement | null => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SentryDraft | null>(null);
  const [showAll, setShowAll] = useState(false);
  const { data: projectData, isLoading, isError: unreachable } = useProjects(source);

  const projects = projectData?.items ?? [];

  const linked = Boolean(product.sentryProjectSlugs?.length || product.sentryBrowserProjectSlug);

  const open = (): void =>
    setDraft({
      sentryOrgSlug: product.sentryOrgSlug ?? null,
      sentryProjectSlugs: product.sentryProjectSlugs ?? [],
      sentryBrowserProjectSlug: product.sentryBrowserProjectSlug ?? null,
    });

  if (draft !== null) {
    // The token sees 34 projects across two organizations, so both boxes open on the product's name.
    const seed = linked ? undefined : searchSeedFor(productName);
    // Filtering hides a project that has no data yet, so "show all" is the way to link one anyway.
    const serverProjects = showAll ? projects : projectsWithMetric(projects, 'transactions', draft.sentryProjectSlugs);
    const browserProjects = showAll
      ? projects
      : projectsWithMetric(projects, 'sessions', [draft.sentryBrowserProjectSlug]);
    const asValues = (slugs: string[]): string[] =>
      slugs.map(slug => `${draft.sentryOrgSlug ?? product.sentryOrgSlug}/${slug}`);
    const emptyKeyFor = (shown: unknown[]): string =>
      t(
        emptyListKey({
          isLoading,
          isError: unreachable,
          hasOptions: shown.length > 0,
          sourceKey: 'connectedApps.noneAvailable',
        }),
      );

    // Every backend project is read in one Sentry organization, so picking one elsewhere starts the list over there.
    // The frontend suggestion follows the first pick, but nothing is written until Save.
    const pickServer = (values: string[]): void => {
      const picked = values.map(value => value.split('/') as [string, string]);
      const current = asValues(draft.sentryProjectSlugs);
      const added = picked.find((_, index) => !current.includes(values[index]));
      const orgSlug = added?.[0] ?? picked[0]?.[0];
      const slugs = picked.filter(([org]) => org === orgSlug).map(([, slug]) => slug);
      setDraft({
        ...draft,
        sentryOrgSlug: orgSlug ?? draft.sentryOrgSlug,
        sentryProjectSlugs: slugs,
        sentryBrowserProjectSlug:
          draft.sentryBrowserProjectSlug ??
          (slugs[0] ? suggestBrowserProject(slugs[0], projects)?.slug : undefined) ??
          null,
      });
    };

    // Every pick sets the org: a browser-only link would otherwise save a null org, and every read needs it.
    const pickBrowser = (value: string | undefined): void => {
      const [orgSlug, projectSlug] = value?.split('/') ?? [];
      setDraft({
        ...draft,
        sentryOrgSlug: projectSlug ? orgSlug : draft.sentryOrgSlug,
        sentryBrowserProjectSlug: projectSlug ?? null,
      });
    };

    return (
      <Picker
        title={t('connectedApps.sentry')}
        onCancel={() => setDraft(null)}
        actions={
          <PickerActions
            // Either one is a working link, so Save waits for at least one.
            canSave={Boolean(draft.sentryProjectSlugs.length || draft.sentryBrowserProjectSlug)}
            isPending={isPending}
            onSave={() => {
              patch(draft);
              setDraft(null);
            }}
            onCancel={() => setDraft(null)}
          />
        }
      >
        <MultiSelectField
          label={t('connectedApps.serverProject')}
          options={serverProjects.map(project => ({
            value: `${project.orgSlug}/${project.slug}`,
            label: projectLabel(project, 'transactions', t('connectedApps.transactions')),
          }))}
          value={asValues(draft.sentryProjectSlugs)}
          onChange={pickServer}
          summaryThreshold={1}
          renderCountSummary={count => t('connectedApps.nProjects', { count })}
          emptyLabel={t('connectedApps.pickProject')}
          searchable
          filter={matchesSubstring}
          searchPlaceholder={t('connectedApps.searchProjects')}
          emptySearchLabel={emptyKeyFor(serverProjects)}
          defaultSearch={seed}
          disabled={isLoading}
        />
        <MultiSelectField
          single
          label={t('connectedApps.browserProject')}
          options={browserProjects.map(project => ({
            value: `${project.orgSlug}/${project.slug}`,
            label: projectLabel(project, 'sessions', t('connectedApps.sessions')),
          }))}
          value={asValues(draft.sentryBrowserProjectSlug ? [draft.sentryBrowserProjectSlug] : [])}
          onChange={values => pickBrowser(values[0])}
          emptyLabel={t('connectedApps.pickBrowserProject')}
          searchable
          filter={matchesSubstring}
          searchPlaceholder={t('connectedApps.searchProjects')}
          emptySearchLabel={emptyKeyFor(browserProjects)}
          defaultSearch={seed}
          disabled={isLoading}
        />
        <div className="flex items-center gap-2">
          <Checkbox id="sentry-show-all" checked={showAll} onCheckedChange={checked => setShowAll(checked === true)} />
          <label htmlFor="sentry-show-all" className="text-xs text-muted-foreground">
            {t('connectedApps.showAllProjects')}
          </label>
        </div>
        {/* A token belongs to a product, so there is nothing to attach one to until the product exists.
            The organization's own picker, in the Add product dialog, keeps reading through Panenco's. */}
        {source.productId && <ExternalToken product={product} productId={source.productId} canEdit={canEdit} />}
      </Picker>
    );
  }

  if (linked) {
    // A browser project alone is a working link, so the row has to read as connected.
    const labels = [...(product.sentryProjectSlugs ?? []), product.sentryBrowserProjectSlug].filter(
      Boolean,
    ) as string[];
    return (
      <Linked
        label={[...new Set(labels)].join(' · ') || t('connectedApps.connected')}
        canEdit={canEdit}
        isPending={isPending}
        onEdit={open}
        onDisconnect={() =>
          // The API drops the org slug and region host with the last link, so the rule lives in one place.
          patch({ sentryProjectSlugs: null, sentryBrowserProjectSlug: null })
        }
      />
    );
  }

  return canEdit ? <ConnectButton onClick={open} disabled={isPending} /> : null;
};

/** A product's connected tools. Every row connects on its own, and connecting one never decides what another reads. */
/** The rows alone, so the Add product dialog renders what the page will and `patch` writes to its draft. */
export const ConnectAppsRows = (props: SeededRowProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <ToolRow icon={GithubIcon} name={t('connectedApps.github')} hint={t('connectedApps.githubHint')}>
        <GithubConnection {...props} />
      </ToolRow>

      <ToolRow icon={AikidoIcon} name={t('connectedApps.aikido')} hint={t('connectedApps.aikidoHint')}>
        <AikidoConnection {...props} />
      </ToolRow>

      <ToolRow icon={SentryIcon} name={t('connectedApps.sentry')} hint={t('connectedApps.sentryHint')}>
        <SentryConnection {...props} />
      </ToolRow>
    </div>
  );
};

const ConnectedApps = ({ product, canEdit }: { product: ProductEntity; canEdit: boolean }): ReactElement => {
  const { t } = useTranslation();
  const save = useUpdateProductDetail(product.id);
  const [failed, setFailed] = useState<string | null>(null);

  const patch = (values: UpdateProduct): void => {
    setFailed(null);
    save.mutate(values, { onError: () => setFailed(t('connectedApps.saveFailed')) });
  };

  const rowProps = {
    product,
    productName: product.name,
    source: { productId: product.id } as PickerSource,
    canEdit,
    isPending: save.isPending,
    patch,
  };

  return (
    <section className={`${cardClass} mb-6`}>
      <h2 className="text-lg font-bold text-foreground">{t('connectedApps.title')}</h2>
      <p className="mb-4 text-sm text-muted-foreground">{t('connectedApps.hint')}</p>

      <ConnectAppsRows {...rowProps} />

      {failed ? <p className="mt-4 text-sm text-destructive">{failed}</p> : null}
    </section>
  );
};

export default ConnectedApps;
